import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { FolderTemplateService } from '../../services/folder-template.service';
import { ProjectService } from '../../services/project.service';
import { FolderTemplateAssignmentModel, FolderTemplateModel } from '../../models/folder-template.model';
import { ProjectModel } from '../../models/project.model';
import { RoleModel } from '../../services/user.service';

@Component({
  selector: 'app-file-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CheckboxModule,
    ToastModule,
    SelectModule
  ],
  providers: [MessageService],
  templateUrl: './file-settings.html',
  styleUrl: './file-settings.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  activeTab: 'folders' | 'permissions' = 'folders';

  // Folder Templates
  folders: FolderTemplateModel[] = [];
  newFolderName: string = '';
  addingFolder: boolean = false;

  // Projects & Roles
  projects: ProjectModel[] = [];
  roles: RoleModel[] = [];
  selectedProject: ProjectModel | null = null;

  // Assignment matrix: assignmentMap[folderId][roleId] = boolean
  assignmentMap: Record<number, Record<number, boolean>> = {};

  // UI States
  loading: boolean = false;
  loadingAssignments: boolean = false;
  saving: boolean = false;
  deletingId: number | null = null;

  constructor(
    private folderTemplateService: FolderTemplateService,
    private projectService: ProjectService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadInitialData(): void {
    this.loading = true;
    forkJoin({
      folders: this.folderTemplateService.getFolderTemplates(),
      projects: this.projectService.getProjectList(),
      roles: this.projectService.getRoleList()
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ folders, projects, roles }) => {
        this.folders = folders;
        this.projects = projects;
        this.roles = roles;
        this.loading = false;
        if (projects.length > 0) {
          this.selectedProject = projects[0];
          this.loadAssignments(projects[0].projectId!);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load data' });
        this.cdr.markForCheck();
      }
    });
  }

  onProjectChange(): void {
    if (!this.selectedProject?.projectId) return;
    this.loadAssignments(this.selectedProject.projectId);
  }

  private loadAssignments(projectId: number): void {
    this.loadingAssignments = true;
    this.assignmentMap = {};
    this.folderTemplateService.getAssignmentsByProject(projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (assignments) => {
          this.buildAssignmentMap(assignments);
          this.loadingAssignments = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingAssignments = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load assignments' });
          this.cdr.markForCheck();
        }
      });
  }

  private buildAssignmentMap(assignments: FolderTemplateAssignmentModel[]): void {
    const map: Record<number, Record<number, boolean>> = {};
    this.folders.forEach(f => {
      map[f.id] = {};
      this.roles.forEach(r => { map[f.id][r.roleId] = false; });
    });
    assignments.forEach(a => {
      if (!map[a.folderTemplateId]) map[a.folderTemplateId] = {};
      map[a.folderTemplateId][a.roleId] = true;
    });
    this.assignmentMap = map;
  }

  isAssigned(folderId: number, roleId: number): boolean {
    return this.assignmentMap[folderId]?.[roleId] ?? false;
  }

  toggleAssignment(folderId: number, roleId: number, value: boolean): void {
    if (!this.assignmentMap[folderId]) this.assignmentMap[folderId] = {};
    this.assignmentMap[folderId][roleId] = value;
  }

  isFolderAllRolesChecked(folderId: number): boolean {
    return this.roles.length > 0 && this.roles.every(r => this.isAssigned(folderId, r.roleId));
  }

  isFolderAllRolesIndeterminate(folderId: number): boolean {
    const count = this.roles.filter(r => this.isAssigned(folderId, r.roleId)).length;
    return count > 0 && count < this.roles.length;
  }

  toggleFolderAllRoles(folderId: number, value: boolean): void {
    this.roles.forEach(r => this.toggleAssignment(folderId, r.roleId, value));
  }

  isRoleAllFoldersChecked(roleId: number): boolean {
    return this.folders.length > 0 && this.folders.every(f => this.isAssigned(f.id, roleId));
  }

  isRoleAllFoldersIndeterminate(roleId: number): boolean {
    const count = this.folders.filter(f => this.isAssigned(f.id, roleId)).length;
    return count > 0 && count < this.folders.length;
  }

  toggleRoleAllFolders(roleId: number, value: boolean): void {
    this.folders.forEach(f => this.toggleAssignment(f.id, roleId, value));
  }

  addFolder(): void {
    const name = this.newFolderName.trim();
    if (!name) return;
    this.addingFolder = true;
    this.folderTemplateService.createFolderTemplate(name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (id) => {
          const newFolder: FolderTemplateModel = { id, folderName: name, isActive: true, hasAssignments: false };
          this.folders = [...this.folders, newFolder];
          // Initialize row in assignment map
          this.assignmentMap[id] = {};
          this.roles.forEach(r => { this.assignmentMap[id][r.roleId] = false; });
          this.newFolderName = '';
          this.addingFolder = false;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Folder added' });
          this.cdr.markForCheck();
        },
        error: () => {
          this.addingFolder = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add folder' });
          this.cdr.markForCheck();
        }
      });
  }

  deleteFolder(folder: FolderTemplateModel): void {
    this.deletingId = folder.id;
    this.folderTemplateService.deleteFolderTemplate(folder.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.folders = this.folders.filter(f => f.id !== folder.id);
          delete this.assignmentMap[folder.id];
          this.deletingId = null;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Folder deleted' });
          this.cdr.markForCheck();
        },
        error: () => {
          this.deletingId = null;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete folder' });
          this.cdr.markForCheck();
        }
      });
  }

  private refreshFolderAssignmentFlags(): void {
    this.folderTemplateService.getFolderTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (folders) => { this.folders = folders; this.cdr.markForCheck(); } });
  }

  saveAssignments(): void {
    if (!this.selectedProject?.projectId) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please select a project first' });
      return;
    }

    const assignments: FolderTemplateAssignmentModel[] = [];
    this.folders.forEach(folder => {
      this.roles.forEach(role => {
        if (this.isAssigned(folder.id, role.roleId)) {
          assignments.push({
            id: 0,
            folderTemplateId: folder.id,
            projectId: this.selectedProject!.projectId!,
            roleId: role.roleId
          });
        }
      });
    });

    this.saving = true;
    this.folderTemplateService.saveAssignments({
      projectId: this.selectedProject.projectId,
      assignments
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Assignments saved' });
        this.refreshFolderAssignmentFlags();
        this.cdr.markForCheck();
      },
      error: () => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save assignments' });
        this.cdr.markForCheck();
      }
    });
  }
}
